import { useState } from 'react';
import { UserProfileFilter, RoleFilter } from '../types';

/**
 * Hook to manage user profile filters and transform them to backend format
 */
export function useUserProfileFilters() {
  const [filters, setFilters] = useState<UserProfileFilter>({});

  /**
   * Transform frontend filters to backend format for GraphQL queries
   */
  const mapFiltersToBackend = (frontendFilters: UserProfileFilter) => {
    if (Object.keys(frontendFilters).length === 0) {
      return undefined;
    }
    
    const backendFilters: any = {};
    
    // Copy simple filters directly
    if (frontendFilters.search) {
      backendFilters.search = frontendFilters.search;
    }
    
    if (frontendFilters.status && frontendFilters.status.length > 0) {
      backendFilters.status = frontendFilters.status;
    }
    
    // Map date filters
    if (frontendFilters.dateAdded) {
      backendFilters.dateAdded = frontendFilters.dateAdded;
    }
    
    if (frontendFilters.dateLastActive) {
      backendFilters.dateLastActive = frontendFilters.dateLastActive;
    }
    
    // Transform role filters from our pill format to backend format
    if (frontendFilters.roles?.length) {
      const roles: { id: string; organizationId?: string; courseId?: string }[] = [];
      
      frontendFilters.roles.forEach(roleFilter => {
        const { roleType, organizationId, courseId } = roleFilter;
        
        // Add role with its specific organizational/course context
        roles.push({ 
          id: roleType,
          organizationId: organizationId || undefined,
          courseId: courseId || undefined
        });
      });
      
      // Only add the roles - let the backend handle organizational scope within role filtering
      if (roles.length > 0) {
        backendFilters.roles = roles;
      }
    }
    
    return backendFilters;
  };
  
  /**
   * Sanitize filters by removing duplicates and combining where possible
   */
  const sanitizeFilters = (filtersToSanitize: UserProfileFilter): UserProfileFilter => {
    const sanitized = { ...filtersToSanitize };
    
    // Deduplicate role filters - if multiple filters with the same role type exist,
    // keep the most specific one (with both org and course if available)
    if (sanitized.roles?.length) {
      const roleTypeMap = new Map<string, RoleFilter>();
      
      // Process each role filter, keeping the most specific one
      sanitized.roles.forEach(roleFilter => {
        const key = roleFilter.roleType;
        const existing = roleTypeMap.get(key);
        
        // If no existing filter for this role type, or this one is more specific, keep it
        if (!existing || 
            (roleFilter.organizationId && !existing.organizationId) || 
            (roleFilter.courseId && !existing.courseId)) {
          roleTypeMap.set(key, roleFilter);
        }
      });
      
      // Convert back to array
      sanitized.roles = Array.from(roleTypeMap.values());
    }
    
    // For date and status filters, ensure only one of each type exists
    // If there are duplicates in the UI, this ensures only the last one is used
    
    // Nothing special needed for status and date filters - they should already
    // be correctly managed by the UI to prevent duplicates
    
    return sanitized;
  };
  
  const handleFilterChange = (newFilters: UserProfileFilter) => {
    // Sanitize the filters before setting them
    const sanitizedFilters = sanitizeFilters(newFilters);
    setFilters(sanitizedFilters);
  };
  
  return {
    filters,
    setFilters: handleFilterChange,
    mapFiltersToBackend,
  };
} 